import React, { useRef, useImperativeHandle, forwardRef, useEffect, useState } from 'react';
import { MasterFlightRecord, getRouteString } from '../../../../utils/types';
import { showValidationToasts } from '../../../../utils/validationMessages';
import { BasicInfoTab, BasicInfoTabRef } from './BasicInfoTab/BasicInfoTab';
import { StationsTab, StationsTabRef } from './StationsTab';
import { FinalPreviewTab } from './FinalPreviewTab';
import { FrequencyToolsTab } from './FrequencyToolsTab';
import { useTimezone } from '../../../../contexts/TimezoneContext';
import { getMYTDayShift, computeUTCFrequency, computeLocalFrequency } from '../../../../utils/timeUtils';
import CloseIcon from '@mui/icons-material/Close';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';

interface RecordFormModalProps {
  open: boolean;
  editMode: boolean;
  isVariantMode: boolean;
  isAdhocMode: boolean;
  activeTab: 'basic' | 'stations' | 'preview';
  setActiveTab: React.Dispatch<React.SetStateAction<'basic' | 'stations' | 'preview'>>;
  draft: MasterFlightRecord;
  setDraft: React.Dispatch<React.SetStateAction<MasterFlightRecord>>;
  error: string | null;
  onClose: () => void;
  onSave: () => void;
}

export interface RecordFormModalRef {
  triggerValidation: () => void;
  resetValidation: () => void;
}

export const RecordFormModal = forwardRef<RecordFormModalRef, RecordFormModalProps>(
  (
    {
      open,
      editMode,
      isVariantMode,
      isAdhocMode,
      activeTab,
      setActiveTab,
      draft,
      setDraft,
      error,
      onClose,
      onSave,
    },
    ref
  ) => {
    const basicInfoTabRef = useRef<BasicInfoTabRef>(null);
    const stationsTabRef = useRef<StationsTabRef>(null);
    const contentRef = useRef<HTMLDivElement>(null);

    const steps = ['basic', 'stations', 'preview'] as const;
    const [highestStepReached, setHighestStepReached] = useState(editMode ? 2 : 0);
    const [showFrequencyTools, setShowFrequencyTools] = useState(false);
    const [parentDateRange, setParentDateRange] = useState<{ startDate: string; endDate: string } | null>(null);

    // Build route string from stations (e.g. "MNL → CEB → MNL")
    const route = getRouteString(draft.stations);

    // Reset highest step when modal opens or mode changes
    useEffect(() => {
      if (open) {
        setHighestStepReached(editMode ? 2 : 0);
      }
    }, [open, editMode]);

    // Track the highest step the user has reached
    useEffect(() => {
      const currentIndex = steps.indexOf(activeTab as typeof steps[number]);
      if (currentIndex >= 0 && currentIndex > highestStepReached) {
        setHighestStepReached(currentIndex);
      }
    }, [activeTab]);

    // Expose validation methods to parent
    useImperativeHandle(ref, () => ({
      triggerValidation: () => {
        basicInfoTabRef.current?.validateAllFields();
        stationsTabRef.current?.validateAllStations();
      },
      resetValidation: () => {
        basicInfoTabRef.current?.resetValidation();
        stationsTabRef.current?.resetValidation();
      },
    }));

    // Scroll to top when switching tabs
    useEffect(() => {
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
    }, [activeTab]);

    // Create-mode MYT frequency fix: when the user selects frequency days on
    // BasicInfoTab (no station data yet, so stored without UTC conversion), then
    // enters station STD on StationsTab, retroactively convert the frequency.
    // This must live here (not in SchedulePeriodSection) because BasicInfoTab
    // unmounts when switching tabs — it can't detect the dayShift transition.
    const { timezone } = useTimezone();
    const isMYT = timezone === 'MYT';
    const dayShift = isMYT ? getMYTDayShift(draft.stations?.[0]?.std) : 0;

    useEffect(() => {
      // Only for create mode — edit mode already has correct UTC frequency
      if (editMode) return;
      // Only when MYT is active and there's an actual day shift
      if (!isMYT || dayShift === 0) return;
      if (!draft.frequency) return;
      // If frequencyMYT is already set, the conversion was already done
      // (either by this effect or by the FrequencyDaySelector's onChange)
      if (draft.frequencyMYT) return;

      const stdUTC = draft.stations?.[0]?.std;
      if (!stdUTC) return;

      const offset = draft.stations?.[0]?.utcLocalTimeVariationDep || '+0800';
      // The user selected these days thinking they were MYT (MYT mode was active)
      // but they were stored without conversion because stdUTC wasn't available.
      // Convert from MYT days → UTC days now.
      const utcFreq = computeUTCFrequency(draft.frequency, stdUTC, offset);
      if (utcFreq !== draft.frequency) {
        const mytFreq = computeLocalFrequency(utcFreq, stdUTC, offset);
        setDraft(prev => ({
          ...prev,
          frequency: utcFreq,
          frequencyMYT: mytFreq,
        }));
      }
    }, [editMode, isMYT, dayShift, draft.frequency, draft.frequencyMYT, draft.stations]); // eslint-disable-line react-hooks/exhaustive-deps

    if (!open) return null;

    const updateDraft = <K extends keyof MasterFlightRecord>(
      key: K,
      value: MasterFlightRecord[K]
    ) => {
      setDraft(prev => ({ ...prev, [key]: value }));
    };

    // Handle Next button - validates current tab before proceeding
    const handleNext = () => {
      if (activeTab === 'basic') {
        const hasErrors = basicInfoTabRef.current?.validateAllFields();
        if (hasErrors) {
          showValidationToasts(draft, 'basic');
        } else {
          setActiveTab('stations');
        }
      } else if (activeTab === 'stations') {
        const hasErrors = stationsTabRef.current?.validateAllStations();
        if (hasErrors) {
          showValidationToasts(draft, 'stations');
        } else {
          setActiveTab('preview');
        }
      }
    };

    // Handle tab click - validates when going forward, free navigation backward
    const handleTabClick = (tab: typeof steps[number]) => {
      const targetIndex = steps.indexOf(tab);
      const currentIndex = steps.indexOf(activeTab as typeof steps[number]);

      // Going backward is always allowed
      if (targetIndex <= currentIndex) {
        setActiveTab(tab);
        return;
      }

      // Going forward: validate current step before allowing navigation
      if (activeTab === 'basic') {
        const hasErrors = basicInfoTabRef.current?.validateAllFields();
        if (hasErrors) {
          showValidationToasts(draft, 'basic');
          return;
        }
      }
      if (activeTab === 'stations' || (activeTab === 'basic' && tab === 'preview')) {
        // If jumping from basic to preview, also validate stations
        if (activeTab === 'basic' && tab === 'preview') {
          // In edit mode, stations are already filled — skip forced routing
          if (!editMode) {
            setActiveTab('stations');
            return;
          }
        }
        if (activeTab === 'stations') {
          const hasErrors = stationsTabRef.current?.validateAllStations();
          if (hasErrors) {
            showValidationToasts(draft, 'stations');
            return;
          }
        }
      }

      setActiveTab(tab);
    };

    // Handle Back button
    const handleBack = () => {
      if (activeTab === 'stations') {
        setActiveTab('basic');
      } else if (activeTab === 'preview') {
        setActiveTab('stations');
      }
    };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center px-5 py-5 z-[200] animate-fade-in"
      onClick={onClose}
    >
      <div
        className="w-full max-h-[90vh] bg-white rounded-2xl shadow-2xl animate-slide-up flex flex-col form-modal"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex-shrink-0 px-6 py-5 border-b border-gray-200 flex items-center justify-between bg-gray-50 rounded-t-2xl form-modal-header">
          <div>
            <h2 className="m-0 text-2xl font-semibold text-gray-900 flex items-center gap-2">
              {(isAdhocMode || draft.isAdhoc) && (
                <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded-md text-sm font-bold uppercase">Adhoc</span>
              )}
              {isAdhocMode ? 'New Adhoc Flight' : editMode ? 'Edit Flight Record' : 'New Flight Record'}
            </h2>
            <p className="m-0 mt-1 text-md text-gray-500">
              {isAdhocMode
                ? 'Search for a parent flight, then customize dates and times'
                : editMode ? `Editing ${draft.flightNo || 'record'}` : 'Fill in the flight schedule details'}
            </p>
          </div>

          <button
            onClick={onClose}
            className="w-9 h-9 border border-gray-200 rounded-lg bg-white text-gray-500 flex items-center justify-center cursor-pointer hover:opacity-95"
          >
            <CloseIcon sx={{ fontSize: 24 }} />
          </button>
        </div>

        {/* Error - Only shows for API errors, not validation errors */}
        {error && (
          <div className="mx-6 mt-4 px-4 py-3 bg-danger-50 border border-danger-100 rounded-lg text-danger-500 text-lg flex items-center gap-2.5">
            <WarningAmberIcon sx={{ fontSize: 20 }} />
            {error}
          </div>
        )}

        {/* Tabs */}
        <div className="flex-shrink-0 px-6 pt-2 flex items-center border-b border-gray-200 bg-gray-50 overflow-x-auto form-modal-header">
          <div className="flex gap-1 flex-1">
            {(['basic', 'stations', 'preview'] as const).map((tab, index) => {
              const currentIndex = steps.indexOf(tab);
              const isAccessible = currentIndex <= highestStepReached;

              return (
                <button
                  key={tab}
                  onClick={() => isAccessible && handleTabClick(tab)}
                  disabled={!isAccessible}
                  className={`px-5 py-3 rounded-t-lg text-md font-semibold relative
                    ${!isAccessible ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}
                    ${activeTab === tab
                      ? 'bg-blue-50 text-primary-600'
                      : isAccessible
                        ? 'bg-transparent text-gray-500 hover:bg-gray-100'
                        : 'bg-transparent text-gray-400'
                    }`}
                >
                  <div className="flex items-center gap-2">
                    {/* Step number */}
                    <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold
                      ${activeTab === tab
                        ? 'bg-primary-500 text-white'
                        : isAccessible
                          ? 'bg-gray-200 text-gray-600'
                          : 'bg-gray-100 text-gray-400'
                      }`}
                    >
                      {index + 1}
                    </span>
                    {tab === 'basic' && 'Basic Info'}
                    {tab === 'stations' && `Stations (${draft.stations.length})`}
                    {tab === 'preview' && 'Final Preview'}
                  </div>
                  {/* Blue underline for active tab */}
                  {activeTab === tab && (
                    <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary-500"></div>
                  )}
                </button>
              );
            })}
          </div>

          {/* Operating Dates button - visible on stations & preview tabs */}
          {activeTab !== 'basic' && (
            <button
              type="button"
              onClick={() => setShowFrequencyTools(true)}
              disabled={!draft.frequency || !draft.startDate || !draft.endDate}
              title="View operating dates"
              className="mb-1 px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-colors border border-gray-200 bg-white text-gray-600 hover:bg-gray-100 hover:border-primary-300 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-200 cursor-pointer"
            >
              <CalendarMonthIcon sx={{ fontSize: 18 }} className="text-primary-600" />
              <span className="hidden sm:inline">Operating Dates</span>
            </button>
          )}
        </div>

        {/* Operating Dates Modal */}
        {showFrequencyTools && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center px-5 py-5 z-[300] animate-fade-in"
            onClick={() => setShowFrequencyTools(false)}
          >
            <div
              className="w-full bg-white rounded-2xl shadow-2xl animate-slide-up flex flex-col"
              style={{ maxHeight: '70vh' }}
              onClick={e => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="flex-shrink-0 px-5 py-3.5 border-b border-gray-200 flex items-center justify-between bg-gray-50 rounded-t-2xl">
                <div className="flex items-center gap-2">
                  <CalendarMonthIcon className="text-primary-600" sx={{ fontSize: 22 }} />
                  <h3 className="text-lg font-semibold text-gray-900">Operating Dates</h3>
                </div>
                <button
                  onClick={() => setShowFrequencyTools(false)}
                  className="w-8 h-8 border border-gray-200 rounded-lg bg-white text-gray-500 flex items-center justify-center cursor-pointer hover:opacity-95"
                >
                  <CloseIcon sx={{ fontSize: 20 }} />
                </button>
              </div>

              {/* Modal Body */}
              <div className="flex-1 min-h-0 overflow-hidden">
                <FrequencyToolsTab
                  frequency={draft.frequency}
                  frequencyMYT={draft.frequencyMYT}
                  startDate={draft.startDate}
                  endDate={draft.endDate}
                  route={route}
                  stdUTC={draft.stations?.[0]?.std}
                />
              </div>
            </div>
          </div>
        )}

        {/* Content */}
        <div ref={contentRef} className="px-6 py-6 overflow-y-auto flex-1 min-h-0 scrollbar-thin">
          {activeTab === 'basic' && (
            <BasicInfoTab
              ref={basicInfoTabRef}
              draft={draft}
              updateDraft={updateDraft}
              setDraft={setDraft}
              editMode={editMode}
              isVariantMode={isVariantMode}
              isAdhocMode={isAdhocMode}
              parentDateRange={parentDateRange}
              setParentDateRange={setParentDateRange}
            />
          )}
          {activeTab === 'stations' && <StationsTab ref={stationsTabRef} draft={draft} setDraft={setDraft} />}
          {activeTab === 'preview' && <FinalPreviewTab draft={draft} />}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-2xl flex justify-between items-center form-modal-footer">
          {/* Left side - Back or Cancel */}
          <div>
            {activeTab === 'basic' ? (
              <button
                onClick={onClose}
                className="px-4 py-2 bg-white border border-gray-200 rounded-md text-gray-600 text-md font-medium cursor-pointer hover:opacity-95"
              >
                Cancel
              </button>
            ) : (
              <button
                onClick={handleBack}
                className="px-4 py-2 bg-white border border-gray-200 rounded-md text-gray-600 text-md font-medium cursor-pointer hover:opacity-95 flex items-center gap-2"
              >
                <ArrowBackIcon sx={{ fontSize: 20 }} />
                Back
              </button>
            )}
          </div>

          {/* Right side - Next or Save */}
          <div className="flex gap-3">
            {activeTab !== 'basic' && (
              <button
                onClick={onClose}
                className="px-4 py-2 bg-white border border-gray-200 rounded-md text-gray-500 text-md font-medium cursor-pointer hover:opacity-95"
              >
                Cancel
              </button>
            )}

            {activeTab === 'preview' ? (
              <button
                onClick={onSave}
                className="px-6 py-2.5 bg-primary-500 border-none rounded-md text-white text-lg font-semibold cursor-pointer hover:opacity-95 flex items-center gap-2"
              >
                <SaveIcon sx={{ fontSize: 20 }} />
                {editMode ? 'Update Record' : 'Save Record'}
              </button>
            ) : (
              <button
                onClick={handleNext}
                className="px-6 py-2.5 bg-primary-500 border-none rounded-md text-white text-lg font-semibold cursor-pointer hover:opacity-95 flex items-center gap-2"
              >
                Next
                <ArrowForwardIcon sx={{ fontSize: 20 }} />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

RecordFormModal.displayName = 'RecordFormModal';
